import React from 'react';
import DataObjectParser from 'dataobject-parser';
import ReactDom from 'react-dom';
import axios from 'axios';
import EmbeddedField from './embedded-field';
import { unsafeListen, unsafeSend } from './iframes/communication';
import unpick from './utilities/unpick';
import serialize from './serialize';
import { getContentLength } from './utilities/utils';
import { globalConfig, stringifyToQueryParams } from './config';
import ErrorBoundary from './utilities/error-boundary';
import trackEvent, { sendAnalyticsEvent, ANALYTICS_EVENTS } from './utilities/analytics';
import { filterFramesByLocation } from './iframes/filters';
import { getCheckSumIndex } from './utilities/hash-utils';
import deepMerge from './utilities/deep_merge';
import createVaultUrl from './utilities/vault-url';
import { getFormState, notifyFieldsAboutChange } from './iframes/iframes-api';
import { GlobalContextProvider } from './global-state';
import { traverseSubmitData } from './utilities/traverse-submit-data';

let currentState = {};

// how often consume telemetry from peer iframes
const TELEMETRY_CONSUME_MS = 100;

// how often to send consumed telemetry to remote
const TELEMETRY_SEND_MS = 1000;

window.getValue = function getValue() {
  return currentState;
};

window.cardType = '';

window.consumeTelemetry = function getTelemetry() {
  const telemetry = window.telemetry;
  window.telemetry = [];
  return telemetry;
};

function updateGlobalValue(props, state, eventType) {
  let serializers = props.serializers || [];
  if (props.type === 'card-number' && serializers.length === 0) {
    serializers.push({ name: 'replace', options: { old: ' ', new: '' } });
  }
  const valueChanged = currentState?.value !== state.value;
  currentState = {
    formId: props.formId,
    name: props.name,
    serializers: serializers,
    value: state.value,
  };
  const updateData = unpick(state, ['value']);
  updateData.name = props.name;
  updateData.formId = props.formId;
  unsafeSend(window.parent, 'update', updateData);
  try {
    if (valueChanged || eventType === 'init') {
      notifyFieldsAboutChange(globalConfig.formId, props.name, state.value);
    }
    // eslint-disable-next-line no-empty
  } catch (e) {}
}

let storedTelemetry = [];
function consumeTelemetryJob() {
  const frames = filterFramesByLocation(window.parent.frames, window.location.origin);
  const telemetry = frames
    .map((frame) => {
      let chunk;
      try {
        chunk = frame.consumeTelemetry();
      } catch (e) {
        /* not initialized yet? */
      }
      return chunk;
    })
    .reduce((p, c) => {
      if (Array.isArray(c)) p.push(...c.filter(Boolean));
      return p;
    }, []);

  storedTelemetry.push(...telemetry);
  setTimeout(consumeTelemetryJob, TELEMETRY_CONSUME_MS);
}

function sendTelemetryJob() {
  if (storedTelemetry.length) {
    sendAnalyticsEvent(storedTelemetry);
    storedTelemetry = [];
  }
  setTimeout(sendTelemetryJob, TELEMETRY_SEND_MS);
}

const handleSeparateSerialization = async (field, merge, callback) => {
  const value = await serialize(field.value, field.serializers);
  for (const prop of Object.keys(value)) {
    if (!merge) {
      callback(prop, value[prop]);
    } else {
      callback(`${field.name}.${prop}`, value[prop]);
    }
  }
};
const mergeDataFromFields = async (formId, data, mapDotToObject, hasCustomBodyCallback) => {
  const formData = getFormState(formId);

  let formData2 = {};
  if (mapDotToObject) {
    // use hierarchy structured Object
    let result = new DataObjectParser();
    for (let i = 0; i < formData.length; i++) {
      const field = formData[i];
      const separateSerializer = field.serializers.find((s) => s.name === 'separate');
      if (separateSerializer) {
        await handleSeparateSerialization(field, true, (name, value) => {
          result.set(name, value);
        });
      } else {
        result.set(field.name, await serialize(field.value, field.serializers));
      }
    }
    formData2 = result.data();
  } else {
    // return an object with flat keys
    for (let i = 0; i < formData.length; i++) {
      const field = formData[i];
      const separateSerializer = field.serializers.find((s) => s.name === 'separate');
      if (separateSerializer && separateSerializer.options.flat) {
        await handleSeparateSerialization(field, false, (name, value) => {
          formData2[name] = value;
        });
      } else {
        formData2[field.name] = await serialize(field.value, field.serializers);
      }
    }
  }

  if (hasCustomBodyCallback) {
    return traverseSubmitData(data, formData2);
  }

  if (mapDotToObject === 'merge') {
    return deepMerge(data, formData2);
  }

  if (mapDotToObject === 'mergeArray') {
    return deepMerge(data, formData2, true);
  }

  return Object.assign({}, data, formData2);
};

function convertToFormData(obj, form, namespace) {
  const formData = form || new FormData();
  let formKey;
  for (let property in obj) {
    if (Object.hasOwnProperty.call(obj, property)) {
      if (namespace) {
        formKey = namespace + '[' + property + ']';
      } else {
        formKey = property;
      }
      /**
       * If the property is an object, but not a File, use recursivity.
       *  */
      if (typeof obj[property] === 'object' && !(obj[property] instanceof File)) {
        convertToFormData(obj[property], formData, property);
      } else {
        /**
         * if it's a string or a File object
         *  */
        formData.append(formKey, obj[property]);
      }
    }
  }
  return formData;
}

if (globalConfig.telemetry) {
  window.telemetryMain = true;
  consumeTelemetryJob();
  sendTelemetryJob();
} else {
  window.addEventListener(
    'DOMContentLoaded',
    () => {
      let configuration = globalConfig;

      if (configuration.tnt && configuration.env) {
        configuration = unpick(configuration, ['env', 'tnt', 'logLevel', 'satellitePort']);
      }

      // transform serializer to javascript object
      if (configuration.serializers) {
        configuration.serializers = JSON.parse(window.atob(configuration.serializers));
      }

      const el = window.document.getElementById('react');

      function onHTMLEvent(event) {
        unsafeSend(window.parent, event.type, event);
      }

      ReactDom.render(
        <ErrorBoundary>
          <GlobalContextProvider formId={configuration.formId}>
            <EmbeddedField
              onFocusEvent={onHTMLEvent}
              onKeyboardEvent={onHTMLEvent}
              updateGlobalValue={updateGlobalValue}
              {...configuration}
            />
          </GlobalContextProvider>
        </ErrorBoundary>,
        el
      );

      unsafeSend(window.parent, 'fieldReady', { fieldId: configuration.fieldId });
    },
    false
  );

  const callbackIdInfo = {};

  unsafeListen('submit', async (payload) => {
    const {
      callbackId,
      data,
      tntId,
      path,
      environment,
      withCredentials,
      hasCustomBodyCallback,
      analytics: { source = 'vgs-collect', medium = 'vgs-collect' } = 'analytics',
    } = payload;

    const { vgsCollectSessionId, logLevel, satellitePort } = globalConfig;
    const method = payload.method || 'post';
    const mapDotToObject = payload.mapDotToObject || false;
    let userData = await mergeDataFromFields(currentState.formId, data, mapDotToObject, hasCustomBodyCallback);

    const randIdentifier = Date.now();
    const dataHashIndex = getCheckSumIndex(userData);

    callbackIdInfo[callbackId] = {
      dataHashIndex,
      randIdentifier,
    };

    const headerParams = {
      source,
      medium,
      content: LIB_VERSION,
      dataHashIndex,
      randIdentifier,
      vgsCollectSessionId,
    };

    if (logLevel) {
      headerParams.tr = logLevel;
    }

    const analyticsHeader = {
      'VGS-Client': stringifyToQueryParams(headerParams),
    };

    const headers = Object.assign({}, payload.headers, analyticsHeader);

    function callback(data) {
      unsafeSend(window.parent, callbackId, data);
    }

    // by default for body request will generate JSON
    switch (payload.serialization) {
      case 'formData':
        userData = convertToFormData(userData);
        break;
      default:
    }

    window.vaultUrl = createVaultUrl({
      vaultUrl: window.vaultUrl,
      tntId,
      environment,
      satellitePort,
    });

    trackEvent({
      type: ANALYTICS_EVENTS.BEFORE_SUBMIT,
      status: 'Ok',
      dataHashIndex,
      randIdentifier,
      callbackId,
    });

    const fetchStarted = Date.now();
    const getLatency = () => Date.now() - fetchStarted;
    const url = `${window.vaultUrl}${path.startsWith('/') ? path : `/${path}`}`;

    axios({
      method: method,
      url,
      data: userData,
      headers: headers,
      withCredentials: withCredentials || false,
    })
      .then((response) => {
        callback({
          status: response.status,
          data: response.data,
        });
        trackEvent({
          type: ANALYTICS_EVENTS.SUBMIT,
          status: 'Ok',
          responseStatus: response.status,
          contentLength: getContentLength(response.data),
          dataHashIndex,
          randIdentifier,
          callbackId,
          latency: getLatency(),
        });
      })
      .catch((error) => {
        /*
         * The request was made and the server responded with a
         * status code that falls out of the range of 2xx
         */
        if (error.response) {
          callback({
            status: error.response.status,
            data: error.response.data,
          });
          trackEvent({
            type: ANALYTICS_EVENTS.SUBMIT,
            status: 'Failed',
            error: error.message,
            errorCode: error.response.status,
            statusText: error.response.statusText,
            randIdentifier,
            callbackId,
            latency: getLatency(),
          });
        } else {
          // Something happened in setting up the request and triggered an Error
          callback({
            status: null,
            data: error.message,
          });
          trackEvent({
            type: ANALYTICS_EVENTS.SUBMIT,
            status: 'Failed',
            error: error.message,
            randIdentifier,
            callbackId,
            latency: getLatency(),
          });
        }
      });
  });

  unsafeListen('receive-submit', (payload) => {
    const callbackId = payload?.callbackId ?? null;
    trackEvent({
      type: ANALYTICS_EVENTS.RECEIVE_SUBMIT,
      status: 'Ok',
      callbackId,
      ...callbackIdInfo[callbackId],
    });
  });

  unsafeListen('submit-callback-error', (payload) => {
    const callbackId = payload?.callbackId ?? null;
    trackEvent({
      type: ANALYTICS_EVENTS.SUBMIT_CALLBACK_ERROR,
      status: 'Ok',
      error: payload.error,
      callbackId,
      ...callbackIdInfo[callbackId],
    });
  });

  unsafeListen('before-submit-callback', (payload) => {
    const callbackId = payload?.callbackId ?? null;
    trackEvent({
      type: ANALYTICS_EVENTS.BEFORE_SUBMIT_CALLBACK,
      status: 'Ok',
      callbackId,
      ...callbackIdInfo[callbackId],
    });
  });

  unsafeListen('after-submit-callback', (payload) => {
    const callbackId = payload?.callbackId ?? null;
    trackEvent({
      type: ANALYTICS_EVENTS.AFTER_SUBMIT_CALLBACK,
      status: 'Ok',
      callbackId,
      ...callbackIdInfo[callbackId],
    });
  });

  unsafeListen('callback-timeout', (payload) => {
    const callbackId = payload?.callbackId ?? null;
    trackEvent({
      type: ANALYTICS_EVENTS.CALLBACK_TIMEOUT,
      status: 'timeout',
      callbackId,
      ...callbackIdInfo[callbackId],
    });
  });
}
